import { useState } from "react";
import axios from "axios";
import {
  User,
  Mail,
  Phone,
  Lock,
  IdCard,
  ShieldCheck,
  Loader2,
  CheckCircle,
  XCircle,
} from "lucide-react";
import Layout, { Container } from "../components/layout";
import { apiurl } from "../config/config";
import { useNavigate } from "react-router-dom";

const CreateRecruiter = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");


 const navigation = useNavigate();
  const [user, setUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    userType: "admin",
    role: "recruiter",
    employeeDetails: {
      employeeId: "",
    },
  });

  const permissions = [
    {
      resource: "dashboard",
      read: true,
      write: false,
      update: false,
      delete: false,
    },
    {
      resource: "application",
      read: true,
      write: false,
      update: false,
      delete: false,
    },
  ];

  const handleChange = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handleEmployeeChange = (e) => {
    setUser({
      ...user,
      employeeDetails: {
        ...user.employeeDetails,
        [e.target.name]: e.target.value,
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      await axios.post(`${apiurl}/api/user/create`, {
        ...user,
        permissions,
      });

      setSuccess("Recruiter account created successfully.");

      navigation("/recruiters");

      setUser({
        ...user,
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        password: "",
        employeeDetails: { employeeId: "", employmentType: "full-time" },
      });
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to create recruiter.");
    } finally {
      setLoading(false);

    }
  };

  const handleEmployeeIdChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // remove non-numbers

    setUser((prev) => ({
      ...prev,
      employeeDetails: {
        ...prev.employeeDetails,
        employeeId: value,
      },
    }));
  };

  return (
    <Layout>
      <Container overflow={"overflow-scroll"}>
        <div className="max-w-4xl mx-auto mt-8 bg-white rounded-2xl shadow-sm border">
          {/* Header */}
          <div className="px-8 py-6 border-b">
            <h2 className="text-2xl font-semibold">Create Recruiter</h2>
            <p className="text-sm text-gray-500 mt-1">
              Recruiters have read-only access to Dashboard & Applications
            </p>
          </div>

          {/* Alerts */}
          <div className="px-8 pt-6 space-y-4">
            {success && (
              <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg">
                <CheckCircle size={18} /> {success}
              </div>
            )}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg">
                <XCircle size={18} /> {error}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="px-8 py-6 space-y-10">
            {/* Basic Info */}
            <section>
              <h3 className="text-lg font-semibold mb-4">Basic Information</h3>

              <div className="grid grid-cols-2 gap-6">
                <Input
                  label="First Name"
                  icon={User}
                  name="firstName"
                  value={user.firstName}
                  onChange={handleChange}
                />
                <Input
                  label="Last Name"
                  icon={User}
                  name="lastName"
                  value={user.lastName}
                  onChange={handleChange}
                />
                <Input
                  label="Official Email"
                  icon={Mail}
                  name="email"
                  type="email"
                  value={user.email}
                  onChange={handleChange}
                  className="col-span-2"
                />
                <Input
                  label="Phone Number"
                  icon={Phone}
                  name="phone"
                  value={user.phone}
                  onChange={handleChange}
                />
                <Input
                  label="Temporary Password"
                  icon={Lock}
                  name="password"
                  type="password"
                  value={user.password}
                  onChange={handleChange}
                />
              </div>
            </section>

            {/* Employee Mapping */}
            <section>
              <h3 className="text-lg font-semibold mb-4">Employee Mapping</h3>

              <div className="grid grid-cols-2 gap-6">
                <Input
                  label="Employee ID"
                  icon={IdCard}
                  name="employeeId"
                  value={user.employeeDetails.employeeId}
                  onChange={handleEmployeeIdChange}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="Numeric Employee ID"
                />

                <DisabledInput label="Employee Code" value={`SUW${user.employeeDetails.employeeId}`} />
                <DisabledInput label="Role" value="Recruiter" />
                <DisabledInput label="Department" value="HR" />
              </div>
            </section>

            {/* Permissions */}
            <section className="bg-gray-50 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={18} className="text-blue-600" />
                <h3 className="font-semibold">Assigned Permissions</h3>
              </div>
              <ul className="list-disc list-inside text-sm text-gray-600">
                <li>Dashboard – View Only</li>
                <li>Applications – View Only</li>
              </ul>
            </section>

            {/* Submit */}
            <button
              disabled={loading}
              className="w-full flex justify-center items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-medium transition disabled:opacity-60"
            >
              {loading && <Loader2 className="animate-spin" size={18} />}
              {loading ? "Creating Recruiter..." : "Create Recruiter"}
            </button>
          </form>
        </div>
      </Container>
    </Layout>
  );
};

/* ---------- Reusable Components ---------- */

const Input = ({ label, icon: Icon, className = "", ...props }) => (
  <div className={`flex flex-col gap-1 ${className}`}>
    <label className="text-sm font-medium text-gray-600">{label}</label>
    <div className="relative">
      <Icon className="absolute left-3 top-3 text-gray-400" size={18} />
      <input
        {...props}
        required
        className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-200 focus:outline-none"
      />
    </div>
  </div>
);

const DisabledInput = ({ label, value }) => (
  <div className="flex flex-col gap-1">
    <label className="text-sm font-medium text-gray-600">{label}</label>
    <input
      value={value}
      disabled
      className="w-full p-2 border rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
    />
  </div>
);

const Select = ({ label, children, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-sm font-medium text-gray-600">{label}</label>
    <select
      {...props}
      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-200"
    >
      {children}
    </select>
  </div>
);

export default CreateRecruiter;
